/**
 * Membership hours utilities for metafield format:
 * { facility_id, start, end, hours, plan, type, daily_limit?, cross_facility? } — start/end are UTC ISO (Z) instants.
 *
 * Period matching uses facility IANA timezone + Day.js when available:
 * slot (date + wall time) → interpreted in facility zone → compared to [start, end) in UTC.
 *
 * Plan rules (hour pool, daily cap, booking window, weekends, cross-facility) are never branched on
 * here — they are read from window.MembershipPlans (`assets/membership-plans.js`), which must load first.
 *
 * Day.js and plugins are loaded from theme assets (`snippets/dayjs-scripts.liquid`); plugins are initialized at the top of this file.
 */
(function initA9DayjsPlugins() {
  if (typeof window === 'undefined') return;
  if (window.__a9DayjsPlugins) return;
  if (typeof dayjs === 'undefined') return;
  if (typeof dayjs_plugin_customParseFormat !== 'undefined') dayjs.extend(dayjs_plugin_customParseFormat);
  if (typeof dayjs_plugin_utc !== 'undefined') dayjs.extend(dayjs_plugin_utc);
  if (typeof dayjs_plugin_timezone !== 'undefined') dayjs.extend(dayjs_plugin_timezone);
  if (typeof dayjs.tz !== 'function') return;
  window.__a9DayjsPlugins = true;
})();

(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.MembershipHoursUtils) return;

  const WEEKDAY_MON = 1;
  const WEEKDAY_FRI = 5;
  const HOURS_DISPLAY_DECIMALS = 2;
  const MINUTES_PER_HOUR = 60;
  const MS_PER_HOUR = 3600000;
  /** A single booking never spans more than a couple of calendar days; guards the day walk. */
  const MAX_SLOT_DAY_SEGMENTS = 3;

  const membershipRowsCache = typeof WeakMap === 'function' ? new WeakMap() : null;
  const membershipCandidateCache = typeof WeakMap === 'function' ? new WeakMap() : null;
  const NO_ROWS = Object.freeze([]);

  /**
   * Plan capabilities live in membership-plans.js, which must load before this file.
   * Without it no plan rule can be evaluated, so every entry point bails out and grants nothing —
   * degrading to "pooled plan, no limits" would hand legacy Unlimited free hours outside its window.
   */
  function getPlans() {
    return typeof window !== 'undefined' ? window.MembershipPlans || null : null;
  }

  function planHasHourPool(entry) {
    const plans = getPlans();
    return plans ? plans.hasHourPool(entry) : true;
  }

  function normalizeSuiteCount(suiteCount) {
    return Number(suiteCount) > 0 ? Number(suiteCount) : 1;
  }

  function roundHours(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    const factor = Math.pow(10, HOURS_DISPLAY_DECIMALS);
    return Math.round(n * factor) / factor;
  }

  function hasDayjsTz() {
    return typeof dayjs !== 'undefined' && typeof dayjs.tz === 'function';
  }

  function currentYmdViaDayjs(facilityTz) {
    if (typeof dayjs === 'undefined') return null;
    const now = facilityTz && hasDayjsTz() ? dayjs().tz(String(facilityTz).trim()) : dayjs();
    return now.isValid() ? now.format('YYYY-MM-DD') : null;
  }

  /** Raw metafield `.value` or the object passed from Liquid/JSON. */
  function unwrapMembershipValue(data) {
    if (data == null) return null;
    if (typeof data.value !== 'undefined') return data.value;
    return data;
  }

  /**
   * Detects membership metafield shape:
   * - `new-array` — post-migration contract: no `period` field; has `start` (usually also `end`).
   * - `legacy-array` — older array rows with local `period` (often still includes Zulu `start`).
   * - `legacy-map` — object `{ "facilityId": hours, ... }` as on the legacy PDP.
   */
  function getMembershipKind(data) {
    const v = unwrapMembershipValue(data);
    if (v == null || v === '') return 'empty';
    if (typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v).length ? 'legacy-map' : 'empty';
    }
    if (!Array.isArray(v) || v.length === 0) return 'empty';
    const first = v[0];
    if (!first || typeof first !== 'object') return 'empty';
    if (Object.prototype.hasOwnProperty.call(first, 'period')) return 'legacy-array';
    if (first.facility_id != null && first.start) return 'new-array';
    return 'legacy-array';
  }

  /** True only for the new backend contract (first row has no `period`). */
  function isNewMembershipFormat(data) {
    return getMembershipKind(data) === 'new-array';
  }

  /** True when rows are a new or legacy array usable for period/slot math (`facility_id` + `start`). */
  function hasStructuredMembershipRows(data) {
    const k = getMembershipKind(data);
    return (k === 'new-array' || k === 'legacy-array') && getMembershipRowsForCalc(data).length > 0;
  }

  /** Member at this facility? A Cincinnati plan must not discount Cornelius. */
  function hasMembershipSlotData(data, facilityId) {
    const k = getMembershipKind(data);
    if (k === 'legacy-map') {
      return facilityId != null && facilityId !== '' && getLegacyFacilityMapHours(data, facilityId) != null;
    }
    if (!hasStructuredMembershipRows(data)) return false;
    return getMembershipCandidates(getMembershipRowsForCalc(data), facilityId).length > 0;
  }

  /** Array rows (new or legacy-array) suitable for half-open intervals [start, end). */
  function getMembershipRowsForCalc(data) {
    const k = getMembershipKind(data);
    if (k !== 'new-array' && k !== 'legacy-array') return NO_ROWS;
    const raw = unwrapMembershipValue(data);
    if (!Array.isArray(raw)) return NO_ROWS;
    if (membershipRowsCache && membershipRowsCache.has(raw)) return membershipRowsCache.get(raw);

    const rows = Object.freeze(raw.filter(function (e) {
      return e && e.facility_id != null && e.start;
    }));
    if (membershipRowsCache) membershipRowsCache.set(raw, rows);
    return rows;
  }

  /** Frozen; excludes the legacy {facilityId: hours} map. */
  function getMembershipEntries(data) {
    return getMembershipRowsForCalc(data);
  }

  function getLegacyFacilityMapHours(data, facilityId) {
    const v = unwrapMembershipValue(data);
    if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
    const fidStr = String(facilityId).trim();
    if (!fidStr) return null;
    if (Object.prototype.hasOwnProperty.call(v, fidStr)) return Number(v[fidStr]);
    const n = parseInt(fidStr, 10);
    if (!isNaN(n) && Object.prototype.hasOwnProperty.call(v, n)) return Number(v[n]);
    return null;
  }

  function getFacilityIdFromProduct(a9osId) {
    if (!a9osId || typeof a9osId !== 'string') return null;
    const s = (a9osId + '').trim();
    if (!s.includes('reservation')) return null;
    const parts = s.replace(/^stage-reservation-/, '').replace(/^reservation-/, '').split('-');
    return parts.length ? parts[parts.length - 1] : null;
  }

  function toYMD(dateInput) {
    if (!dateInput) return '';
    if (typeof dateInput === 'string') {
      const s = dateInput.trim();
      const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return isoMatch[1] + '-' + isoMatch[2] + '-' + isoMatch[3];
      const mdyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdyMatch) {
        const m = String(Number(mdyMatch[1])).padStart(2, '0');
        const dNum = String(Number(mdyMatch[2])).padStart(2, '0');
        return mdyMatch[3] + '-' + m + '-' + dNum;
      }
    }
    const d = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function toDateOnly(dateInput) {
    return toYMD(dateInput);
  }

  function toPeriodDateStr(input) {
    return toYMD(input);
  }

  /** Calendar YYYY-MM-DD for an instant, in facility zone (or UTC prefix fallback). */
  function ymdForInstantInZone(isoOrMs, facilityTz) {
    if (facilityTz && hasDayjsTz()) {
      const d = dayjs(isoOrMs);
      if (d.isValid()) {
        const z = String(facilityTz).trim();
        const local = d.tz(z);
        if (local.isValid()) return local.format('YYYY-MM-DD');
      }
    }
    return toPeriodDateStr(typeof isoOrMs === 'number' ? new Date(isoOrMs).toISOString() : isoOrMs);
  }

  function normalizeInputDateToYmd(dateInput, facilityTz) {
    if (dateInput instanceof Date) {
      if (facilityTz && hasDayjsTz()) {
        const z = String(facilityTz).trim();
        const w = dayjs.utc(dateInput).tz(z);
        return w.isValid() ? w.format('YYYY-MM-DD') : toYMD(dateInput);
      }
      return toYMD(dateInput);
    }
    return toYMD(dateInput);
  }

  /**
   * Facility-local day as a UTC range. The end is the next calendar day's local midnight, never
   * start + 24h — DST days run 23h or 25h.
   */
  function localDayBoundsUtcMs(ymd, facilityTz) {
    if (!ymd || !facilityTz || !hasDayjsTz()) return null;
    const z = String(facilityTz).trim();
    const start = dayjs.tz(ymd + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!start.isValid()) return null;
    const nextYmd = dayjs(ymd, 'YYYY-MM-DD', true);
    if (!nextYmd.isValid()) return null;
    const endExclusive = dayjs.tz(nextYmd.add(1, 'day').format('YYYY-MM-DD') + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', z);
    if (!endExclusive.isValid()) return null;
    return { startMs: start.valueOf(), endExclusiveMs: endExclusive.valueOf() };
  }

  /** Facility-local midnight of `ymd` as a clean Zulu instant, the format every period row uses. */
  function localDayStartZulu(ymd, facilityTz) {
    const bounds = localDayBoundsUtcMs(ymd, facilityTz);
    if (!bounds) return '';
    return new Date(bounds.startMs).toISOString().replace(/\.\d{3}Z$/, 'Z');
  }

  function parseSlotUtcMs(dateStr, timeStr, facilityTz) {
    if (!dateStr || !timeStr || !facilityTz || !hasDayjsTz()) return null;
    const z = String(facilityTz).trim();
    const ymd = normalizeInputDateToYmd(dateStr, null);
    if (!ymd) return null;
    const t = String(timeStr).trim();
    let m = dayjs.tz(ymd + ' ' + t, 'YYYY-MM-DD h:mm A', z);
    if (!m.isValid()) m = dayjs.tz(ymd + ' ' + t, 'YYYY-MM-DD H:mm', z);
    if (!m.isValid()) m = dayjs.tz(ymd + ' ' + t, 'YYYY-MM-DD HH:mm:ss', z);
    return m.isValid() ? m.valueOf() : null;
  }

  function entryBoundsMs(entry, nextSameFacilitySorted) {
    const startMs = Date.parse(entry.start);
    let endMs = entry.end ? Date.parse(entry.end) : NaN;
    if (!Number.isFinite(endMs) && nextSameFacilitySorted && nextSameFacilitySorted.start) {
      endMs = Date.parse(nextSameFacilitySorted.start);
    }
    if (!Number.isFinite(endMs) && entry) {
      const legacyEx = legacyPeriodExclusiveEndUtcMs(entry);
      if (Number.isFinite(legacyEx)) endMs = legacyEx;
    }
    return {
      startMs: startMs,
      endMsExclusive: Number.isFinite(endMs) ? endMs : Number.POSITIVE_INFINITY
    };
  }

  /** Raw `type`|facility_id — metafield has no membership id; never resolve the plan key (100h+ Pro would split). */
  function membershipGroupKey(entry) {
    return String((entry && entry.type) || '').trim().toLowerCase() + '|' + Number(entry && entry.facility_id);
  }

  function byStartAscending(a, b) {
    return Date.parse(a.start) - Date.parse(b.start);
  }

  /**
   * The backend's own pick wins over our ranking. `GET /slots` answers `meta.membership.plan` and
   * `GET /account/memberships` is ordered by id — either names the membership A9OS will actually
   * charge, which the metafield cannot: its rows carry no membership id and their `start` is a
   * billing-period boundary, not the purchase date, so a renewal can make the older plan look newer.
   * Without a hint the ranking below stands as the estimate.
   */
  function preferCandidateType(candidates, preferredType) {
    const wanted = String(preferredType || '').trim().toLowerCase();
    if (!wanted || candidates.length < 2) return candidates;

    const index = candidates.findIndex(function (candidate) { return candidate.planType === wanted; });
    if (index <= 0) return candidates;

    const reordered = candidates.slice();
    reordered.unshift(reordered.splice(index, 1)[0]);
    return Object.freeze(reordered);
  }

  /**
   * Home first, then network-wide, newest first.
   * A cancelled plan overlaps its replacement until it lapses; first that funds anything wins.
   */
  function getMembershipCandidates(entries, facilityId, preferredType) {
    const fid = typeof facilityId === 'number' ? facilityId : parseInt(facilityId, 10);
    if (isNaN(fid) || !Array.isArray(entries) || !entries.length) return NO_ROWS;

    const cachedByFacility = membershipCandidateCache ? membershipCandidateCache.get(entries) : null;
    if (cachedByFacility && cachedByFacility.has(fid)) return preferCandidateType(cachedByFacility.get(fid), preferredType);

    const plans = getPlans();
    const byKey = new Map();

    entries.forEach(function (entry) {
      const isHomeFacility = Number(entry.facility_id) === fid;
      if (!isHomeFacility && !(plans && plans.isCrossFacility(entry))) return;

      const key = membershipGroupKey(entry);
      if (!byKey.has(key)) {
        byKey.set(key, {
          key: key,
          rows: [],
          planType: String((entry && entry.type) || '').trim().toLowerCase(),
          legacyPlan: false,
          homeFacilityId: Number(entry.facility_id),
          crossFacility: !isHomeFacility
        });
      }
      byKey.get(key).rows.push(entry);
    });

    const candidates = [];
    byKey.forEach(function (candidate) {
      candidate.rows.sort(byStartAscending);
      candidate.legacyPlan = !!(plans && candidate.planType && plans.resolve({ type: candidate.planType }).legacy);
      candidate.minStartMs = Date.parse(candidate.rows[0].start);
      candidate.maxEndMs = candidate.rows.reduce(function (widest, row, i) {
        return Math.max(widest, entryBoundsMs(row, candidate.rows[i + 1] || null).endMsExclusive);
      }, Number.NEGATIVE_INFINITY);
      Object.freeze(candidate.rows);
      candidates.push(candidate);
    });

    candidates.sort(function (a, b) {
      if (a.crossFacility !== b.crossFacility) return a.crossFacility ? 1 : -1;
      if (a.legacyPlan !== b.legacyPlan) return a.legacyPlan ? 1 : -1;
      return b.minStartMs - a.minStartMs;
    });

    Object.freeze(candidates);
    if (membershipCandidateCache) {
      const cache = cachedByFacility || new Map();
      cache.set(fid, candidates);
      membershipCandidateCache.set(entries, cache);
    }
    return preferCandidateType(candidates, preferredType);
  }

  /**
   * Whether the local ranking can be trusted without asking the backend. One candidate has nothing
   * to confuse; two that differ in `legacyPlan` are settled by a rule a renewal cannot skew. Only
   * two equally sellable plans leave the order resting on the billing-period dates.
   */
  function isCandidateRankingCertain(entries, facilityId) {
    const candidates = getMembershipCandidates(entries || [], facilityId);
    if (candidates.length < 2) return true;
    return candidates[0].legacyPlan !== candidates[1].legacyPlan;
  }

  function periodUsableOnDate(period, ymd, facilityTz) {
    if (!period) return false;
    const plans = getPlans();
    if (ymd && plans && plans.forbidsWeekends(period) && !isWeekday(ymd, facilityTz)) return false;
    if (!planHasHourPool(period)) return true;
    return Number(period.hours) > 0;
  }

  function firstCandidateHit(entries, facilityId, resolveWithinCandidate, options) {
    const opts = options || {};
    const candidates = getMembershipCandidates(entries || [], facilityId, opts.preferredType);
    let firstCovering = null;

    for (let i = 0; i < candidates.length; i++) {
      const hit = resolveWithinCandidate(candidates[i].rows, candidates[i]);
      if (!hit) continue;
      const period = opts.readPeriodRow ? opts.readPeriodRow(hit) : hit;
      if (periodUsableOnDate(period, opts.ymd, opts.facilityTz)) return hit;
      if (!firstCovering) firstCovering = hit;
    }
    return firstCovering;
  }

  function findContainingByInstant(sortedFacilityEntries, utcMs) {
    for (let i = 0; i < sortedFacilityEntries.length; i++) {
      const entry = sortedFacilityEntries[i];
      const next = sortedFacilityEntries[i + 1] || null;
      const b = entryBoundsMs(entry, next);
      if (utcMs >= b.startMs && utcMs < b.endMsExclusive) {
        return { period: entry, nextEntry: next };
      }
    }
    return null;
  }

  /** Optional `dayRange` so a candidate loop doesn't recompute local-day bounds. */
  function findContainingLocalDay(sortedFacilityEntries, ymd, facilityTz, dayRange) {
    const range = dayRange || localDayBoundsUtcMs(ymd, facilityTz);
    if (!range) return null;
    for (let i = 0; i < sortedFacilityEntries.length; i++) {
      const entry = sortedFacilityEntries[i];
      const next = sortedFacilityEntries[i + 1] || null;
      const b = entryBoundsMs(entry, next);
      if (range.startMs < b.endMsExclusive && range.endExclusiveMs > b.startMs) return entry;
    }
    return null;
  }

  /** Legacy: next entry with different facility-local start day (UTC-prefix heuristic). */
  function getNextSequentialEntry(forFacility, currentIndex, facilityTz) {
    const entry = forFacility[currentIndex];
    if (!entry) return null;
    const entryStartYmd = facilityTz && hasDayjsTz()
      ? ymdForInstantInZone(entry.start, facilityTz)
      : toPeriodDateStr(entry.start);

    for (let j = currentIndex + 1; j < forFacility.length; j++) {
      const candidate = forFacility[j];
      if (!candidate) continue;
      const candYmd = facilityTz && hasDayjsTz()
        ? ymdForInstantInZone(candidate.start, facilityTz)
        : toPeriodDateStr(candidate.start);
      if (!candYmd) continue;
      if (candYmd === entryStartYmd) continue;
      return candidate;
    }
    return null;
  }

  function parseYmdToLocalDate(ymd) {
    if (!ymd || typeof ymd !== 'string') return null;
    const m = ymd.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    if (!y || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    return new Date(y, mo - 1, d);
  }

  function addMonthsToYmd(ymd, monthsToAdd) {
    const dt = parseYmdToLocalDate(ymd);
    if (!dt) return null;
    const d = new Date(dt.getTime());
    const day = d.getDate();
    d.setMonth(d.getMonth() + monthsToAdd, 1);
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, lastDay));
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return y + '-' + mo + '-' + dd;
  }

  function ymdMinusOneDay(ymd) {
    const dt = parseYmdToLocalDate(ymd);
    if (!dt) return '';
    dt.setDate(dt.getDate() - 1);
    const y = dt.getFullYear();
    const mo = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return y + '-' + mo + '-' + dd;
  }

  /**
   * Legacy array rows: `period` is the month anchor YMD (same as monthly_hours_details in membership-management).
   * Inclusive calendar end = day before (period + 1 month). Also supports object { end } or arbitrary date strings.
   */
  function getLegacyPeriodInclusiveEndYmd(periodField) {
    if (periodField == null || periodField === '') return '';
    if (typeof periodField === 'object' && periodField.end) {
      return toPeriodDateStr(periodField.end);
    }
    if (typeof periodField === 'string') {
      const p = periodField.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(p)) {
        const nextMonthStart = addMonthsToYmd(p, 1);
        if (!nextMonthStart) return '';
        return ymdMinusOneDay(nextMonthStart);
      }
      return toPeriodDateStr(p) || '';
    }
    return '';
  }

  /** Exclusive UTC instant for interval end when there is no next row / no entry.end (half-open [start, end)). */
  function legacyPeriodExclusiveEndUtcMs(entry) {
    if (!entry || entry.period == null) return NaN;
    const p = entry.period;
    if (typeof p === 'object' && p.end) {
      const ms = Date.parse(p.end);
      return Number.isFinite(ms) ? ms : NaN;
    }
    if (typeof p === 'string') {
      const pTrim = p.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(pTrim)) {
        const nextStart = addMonthsToYmd(pTrim, 1);
        if (!nextStart) return NaN;
        return Date.parse(nextStart + 'T00:00:00.000Z');
      }
      const ms = Date.parse(pTrim);
      return Number.isFinite(ms) ? ms : NaN;
    }
    return NaN;
  }

  function getPeriodEndDateStr(entry, nextEntry, facilityTz) {
    // Read the boundary in the facility zone in both branches —
    // `2026-09-01T04:00:00Z` is still August 31 in New York.
    if (entry && entry.end) {
      return facilityTz && hasDayjsTz()
        ? ymdForInstantInZone(entry.end, facilityTz)
        : toPeriodDateStr(entry.end);
    }
    if (nextEntry) {
      return facilityTz && hasDayjsTz()
        ? ymdForInstantInZone(nextEntry.start, facilityTz)
        : toPeriodDateStr(nextEntry.start);
    }
    if (entry && entry.period != null) {
      const inc = getLegacyPeriodInclusiveEndYmd(entry.period);
      if (inc) return inc;
    }
    return '';
  }

  /** Last calendar day of a period: own `end`, else the next period's start. */
  function getPeriodEndYmd(entries, period, facilityTz) {
    if (!period) return '';
    const candidates = getMembershipCandidates(entries || [], period.facility_id);
    let rows = null;
    let index = -1;

    for (let i = 0; i < candidates.length && index < 0; i++) {
      index = candidates[i].rows.indexOf(period);
      if (index >= 0) rows = candidates[i].rows;
    }
    for (let i = 0; i < candidates.length && index < 0; i++) {
      index = candidates[i].rows.findIndex(function (e) { return e.start === period.start; });
      if (index >= 0) rows = candidates[i].rows;
    }

    const nextEntry = index >= 0 ? getNextSequentialEntry(rows, index, facilityTz) : null;
    return getPeriodEndDateStr(period, nextEntry, facilityTz);
  }

  function getMinutesFromStartIso(startIso) {
    if (!startIso || typeof startIso !== 'string') return 0;
    const match = startIso.trim().match(/T(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/i);
    if (!match) return 0;
    const h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    if (h < 0 || h > 23 || m < 0 || m > 59) return 0;
    return h * 60 + m;
  }

  function findPeriodByDateStr(rows, dateOnly) {
    for (let i = 0; i < rows.length; i++) {
      const entry = rows[i];
      const startDateStr = toPeriodDateStr(entry.start);
      if (!startDateStr) continue;
      const nextEntry = getNextSequentialEntry(rows, i, null);
      const periodEndStr = getPeriodEndDateStr(entry, nextEntry, null);
      if (dateOnly < startDateStr) continue;
      if (periodEndStr !== '' && dateOnly > periodEndStr) continue;
      return entry;
    }
    return null;
  }

  function getPeriodForFacilityAndDateLegacy(entries, facilityId, dateStr, facilityTz, preferredType) {
    const dateOnly = toDateOnly(dateStr);
    if (!dateOnly) return null;
    return firstCandidateHit(entries, facilityId, function (rows) {
      return findPeriodByDateStr(rows, dateOnly);
    }, { ymd: dateOnly, facilityTz: facilityTz, preferredType: preferredType });
  }

  function getPeriodForFacilityAndDate(entries, facilityId, date, facilityTz, preferredType) {
    const ymd = normalizeInputDateToYmd(date, facilityTz);
    if (!ymd) return null;

    const dayRange = facilityTz && hasDayjsTz() ? localDayBoundsUtcMs(ymd, facilityTz) : null;
    if (dayRange) {
      const hit = firstCandidateHit(entries, facilityId, function (rows) {
        return findContainingLocalDay(rows, ymd, facilityTz, dayRange);
      }, { ymd: ymd, facilityTz: facilityTz, preferredType: preferredType });
      if (hit) return hit;
    }
    return getPeriodForFacilityAndDateLegacy(entries, facilityId, ymd, facilityTz, preferredType);
  }

  function getPeriodForFacilityAndDateTimeLegacy(entries, facilityId, dateStr, timeStr) {
    const slotDateOnly = toPeriodDateStr(dateStr);
    const slotMinutes = timeToMinutes(timeStr);
    if (!slotDateOnly) return null;

    return firstCandidateHit(entries, facilityId, function (rows) {
      for (let i = 0; i < rows.length; i++) {
        const entry = rows[i];
        const startDateStr = toPeriodDateStr(entry.start);
        const startMinutes = getMinutesFromStartIso(entry.start);
        const nextEntry = getNextSequentialEntry(rows, i, null);
        const periodEndDateStr = getPeriodEndDateStr(entry, nextEntry, null);
        const periodEndMinutes = startMinutes;

        if (slotDateOnly < startDateStr) continue;
        if (slotDateOnly === startDateStr && slotMinutes < startMinutes) continue;
        if (periodEndDateStr === '') return { period: entry, nextEntry: nextEntry };
        if (slotDateOnly > periodEndDateStr) continue;
        if (slotDateOnly === periodEndDateStr && slotMinutes >= periodEndMinutes) continue;
        return { period: entry, nextEntry: nextEntry };
      }
      return null;
    }, {
      ymd: slotDateOnly,
      readPeriodRow: function (hit) { return hit.period; }
    });
  }

  function timeToMinutes(timeStr) {
    if (!timeStr || typeof timeStr !== 'string') return 0;
    const s = timeStr.trim();
    const match12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = parseInt(match12[2], 10);
      const ap = (match12[3] || '').toUpperCase();
      if (ap === 'PM' && h !== 12) h += 12;
      if (ap === 'AM' && h === 12) h = 0;
      return h * 60 + m;
    }
    const match24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (match24) {
      const h = parseInt(match24[1], 10);
      const m = parseInt(match24[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return h * 60 + m;
    }
    return 0;
  }

  function isWeekday(dateStr, facilityTz) {
    const s = String(dateStr || '').trim();
    const ymd = toYMD(s);
    if (facilityTz && hasDayjsTz() && ymd) {
      const z = String(facilityTz).trim();
      const noon = dayjs.tz(ymd + ' 12:00:00', 'YYYY-MM-DD HH:mm:ss', z);
      if (noon.isValid()) {
        const d = noon.day();
        return d >= WEEKDAY_MON && d <= WEEKDAY_FRI;
      }
    }

    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const y = Number(isoMatch[1]);
      const mo = Number(isoMatch[2]);
      const dNum = Number(isoMatch[3]);
      const d = new Date(y, mo - 1, dNum);
      if (isNaN(d.getTime())) return false;
      const day = d.getDay();
      return day >= WEEKDAY_MON && day <= WEEKDAY_FRI;
    }
    const mdyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdyMatch) {
      const mo = Number(mdyMatch[1]);
      const dNum = Number(mdyMatch[2]);
      const y = Number(mdyMatch[3]);
      const d = new Date(y, mo - 1, dNum);
      if (isNaN(d.getTime())) return false;
      const day = d.getDay();
      return day >= WEEKDAY_MON && day <= WEEKDAY_FRI;
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) return false;
    const day = d.getDay();
    return day >= WEEKDAY_MON && day <= WEEKDAY_FRI;
  }

  /** Wall-clock minutes since local midnight for an instant, or null without Day.js tz. */
  function wallMinutesInZone(utcMs, facilityTz) {
    if (!facilityTz || !hasDayjsTz()) return null;
    const local = dayjs(utcMs).tz(String(facilityTz).trim());
    return local.isValid() ? local.hour() * MINUTES_PER_HOUR + local.minute() : null;
  }

  /** Cart bookkeeping key for the daily cap: one membership, one facility-local day. */
  function membershipDayKey(facilityId, ymd) {
    if (facilityId == null || facilityId === '' || !ymd) return null;
    return Number(facilityId) + '|' + ymd;
  }

  /** Cart bookkeeping key for a period balance; mirrors MembershipSlotPricing's period key. */
  function periodBalanceKey(period) {
    if (!period) return '';
    const facilityId = period.facility_id != null ? Number(period.facility_id) : '';
    return facilityId + '|' + period.start;
  }

  /**
   * Booking split into facility-local calendar days: [{ ymd, startMinutes, hours }].
   * `hours` is elapsed time (DST-aware) because allowances count hours, while `startMinutes` is
   * wall clock because the booking window compares clock time.
   * Without Day.js tz there is nothing to split by, so the booking stays one segment.
   */
  function splitSlotByLocalDay(dateStr, timeStr, durationHours, facilityTz) {
    const duration = Number(durationHours);
    if (!Number.isFinite(duration) || duration <= 0) return [];

    const startMs = parseSlotUtcMs(dateStr, timeStr, facilityTz);
    if (startMs == null) {
      return [{ ymd: toYMD(dateStr), startMinutes: timeToMinutes(timeStr), hours: duration }];
    }

    const endMs = startMs + duration * MS_PER_HOUR;
    const segments = [];
    let cursorMs = startMs;

    for (let step = 0; step < MAX_SLOT_DAY_SEGMENTS && cursorMs < endMs; step++) {
      const ymd = ymdForInstantInZone(cursorMs, facilityTz);
      const bounds = localDayBoundsUtcMs(ymd, facilityTz);
      if (!bounds) break;
      const segmentEndMs = Math.min(endMs, bounds.endExclusiveMs);
      const startMinutes = wallMinutesInZone(cursorMs, facilityTz);
      segments.push({
        ymd: ymd,
        startMinutes: startMinutes != null ? startMinutes : 0,
        hours: (segmentEndMs - cursorMs) / MS_PER_HOUR
      });
      cursorMs = segmentEndMs;
    }

    return segments;
  }

  /**
   * Hours of one day segment the plan itself allows, before any balance:
   * elapsed hours clamped to the booking window, zero on a blocked weekend.
   */
  function planBookableHoursForSegment(period, segment, facilityTz) {
    const plans = getPlans();
    if (!plans) return 0;
    if (plans.forbidsWeekends(period) && !isWeekday(segment.ymd, facilityTz)) return 0;

    const windowEndMinutes = plans.windowEndMinutesFor(period);
    if (windowEndMinutes == null) return segment.hours;
    return Math.min(segment.hours, Math.max(0, windowEndMinutes - segment.startMinutes) / MINUTES_PER_HOUR);
  }

  /**
   * A plan with a booking window is evaluated on the first day only: the backend refuses a windowed
   * membership booking that crosses local midnight, so later days must not earn free hours.
   */
  function segmentsToScore(segments, period) {
    const plans = getPlans();
    const hasWindow = !!plans && plans.windowEndMinutesFor(period) != null;
    return hasWindow ? segments.slice(0, 1) : segments;
  }

  /**
   * Membership hours a plan may grant when there is no period balance to draw on — the legacy
   * facility-map format. Day allowances, the booking window and the weekend block all come from
   * the plan; a booking over midnight draws on each day's allowance separately.
   */
  function planFreePaidHours(entry, dateStr, timeStr, durationHours, facilityTz, suiteCount, hoursUsedByDay) {
    const suites = normalizeSuiteCount(suiteCount);
    const duration = Number(durationHours);
    const totalMembershipHours = duration * suites;
    const result = { freeHours: 0, paidHours: totalMembershipHours, isValidWindow: false };
    if (!Number.isFinite(duration) || duration <= 0) return result;

    const plans = getPlans();
    if (!plans) return result;

    const dailyLimit = plans.dailyLimitFor(entry);
    const usedByDay = (typeof hoursUsedByDay === 'object' && hoursUsedByDay !== null) ? hoursUsedByDay : null;
    const facilityId = entry && entry.facility_id != null ? entry.facility_id : null;
    const segments = splitSlotByLocalDay(dateStr, timeStr, duration, facilityTz);

    let freeHours = 0;
    const grantedByDay = {};
    segmentsToScore(segments, entry).forEach(function (segment) {
      const bookableHours = planBookableHoursForSegment(entry, segment, facilityTz);
      if (bookableHours <= 0) return;

      let grant = bookableHours * suites;
      if (dailyLimit != null) {
        const dayKey = membershipDayKey(facilityId, segment.ymd);
        const spentToday = (usedByDay && dayKey ? Number(usedByDay[dayKey] || 0) : 0) + (grantedByDay[dayKey] || 0);
        grant = Math.min(grant, Math.max(0, dailyLimit - spentToday));
        if (dayKey) grantedByDay[dayKey] = (grantedByDay[dayKey] || 0) + grant;
      }
      freeHours += grant;
    });

    result.freeHours = freeHours;
    result.paidHours = Math.max(0, totalMembershipHours - freeHours);
    result.isValidWindow = freeHours > 0;
    return result;
  }

  /** Engine-wide "plan draws from no hour pool" check; the rule lives in the plan registry. */
  function isUnlimitedType(entry) {
    if (!entry) return false;
    return !planHasHourPool(entry);
  }

  function getAvailableHoursForPeriod(entry, hoursUsedInCart) {
    if (!entry) return null;
    if (isUnlimitedType(entry)) return null;
    const h = Number(entry.hours);
    return Math.max(0, (isNaN(h) ? 0 : h) - (hoursUsedInCart || 0));
  }

  /**
   * Booking cut at facility-local day boundaries AND membership-period boundaries. Each segment
   * carries the period that funds it, so a booking over a renewal night draws on both buckets.
   */
  function splitSlotBySegments(dateStr, timeStr, durationHours, facilityTz, sortedEntries, startMsHint) {
    const duration = Number(durationHours);
    if (!Number.isFinite(duration) || duration <= 0) return [];

    const startMs = startMsHint != null ? startMsHint : parseSlotUtcMs(dateStr, timeStr, facilityTz);
    if (startMs == null) return [];
    const endMs = startMs + duration * MS_PER_HOUR;

    const cuts = [startMs, endMs];
    const entries = Array.isArray(sortedEntries) ? sortedEntries : [];
    entries.forEach(function (entry, index) {
      const bounds = entryBoundsMs(entry, entries[index + 1] || null);
      [bounds.startMs, bounds.endMsExclusive].forEach(function (ms) {
        if (Number.isFinite(ms) && ms > startMs && ms < endMs) cuts.push(ms);
      });
    });

    let cursorMs = startMs;
    for (let step = 0; step < MAX_SLOT_DAY_SEGMENTS && cursorMs < endMs; step++) {
      const bounds = localDayBoundsUtcMs(ymdForInstantInZone(cursorMs, facilityTz), facilityTz);
      if (!bounds) break;
      if (bounds.endExclusiveMs > startMs && bounds.endExclusiveMs < endMs) cuts.push(bounds.endExclusiveMs);
      cursorMs = bounds.endExclusiveMs;
    }

    const ordered = cuts.slice().sort(function (a, b) { return a - b; })
      .filter(function (ms, index, all) { return index === 0 || ms !== all[index - 1]; });

    const segments = [];
    for (let i = 0; i < ordered.length - 1; i++) {
      const segStartMs = ordered[i];
      const segEndMs = ordered[i + 1];
      if (segEndMs <= segStartMs) continue;
      const containing = findContainingByInstant(entries, segStartMs);
      const startMinutes = wallMinutesInZone(segStartMs, facilityTz);
      segments.push({
        startMs: segStartMs,
        endMs: segEndMs,
        hours: (segEndMs - segStartMs) / MS_PER_HOUR,
        ymd: ymdForInstantInZone(segStartMs, facilityTz),
        startMinutes: startMinutes != null ? startMinutes : 0,
        period: containing ? containing.period : null
      });
    }
    return segments;
  }

  /**
   * The two balances a segment draws on, read lazily and decremented as the walk proceeds.
   * Day keys are anchored to the membership's own facility so a two-bucket booking cannot split one day.
   */
  function createHourBudgets(homeFacilityId, hoursUsedByPeriod, hoursUsedByDay) {
    const plans = getPlans();
    const usedByDay = hoursUsedByDay || {};
    const periodRemaining = {};
    const dayRemaining = {};

    // Legacy callers pass a single number instead of a per-period map; it then applies to every period.
    const usedHoursForPeriod = function (periodKey) {
      return (typeof hoursUsedByPeriod === 'object' && hoursUsedByPeriod !== null)
        ? Number(hoursUsedByPeriod[periodKey] || 0)
        : Number(hoursUsedByPeriod || 0);
    };

    const budgets = {
      dayKeyFor: function (ymd) {
        return membershipDayKey(homeFacilityId, ymd);
      },
      /** Hours left in the period covering a segment; Infinity for plans without a pool. */
      remainingInPeriod: function (period) {
        const key = periodBalanceKey(period);
        if (periodRemaining[key] === undefined) {
          const available = getAvailableHoursForPeriod(period, usedHoursForPeriod(key));
          periodRemaining[key] = available == null ? Number.POSITIVE_INFINITY : available;
        }
        return periodRemaining[key];
      },
      /** Hours left in a facility-local day; Infinity when the plan has no daily cap. */
      remainingInDay: function (period, ymd) {
        const dailyLimit = plans ? plans.dailyLimitFor(period) : null;
        const key = membershipDayKey(homeFacilityId, ymd);
        if (dailyLimit == null || !key) return Number.POSITIVE_INFINITY;
        if (dayRemaining[key] === undefined) {
          dayRemaining[key] = Math.max(0, dailyLimit - Number(usedByDay[key] || 0));
        }
        return dayRemaining[key];
      },
      spend: function (period, ymd, hours) {
        periodRemaining[periodBalanceKey(period)] = budgets.remainingInPeriod(period) - hours;
        const key = membershipDayKey(homeFacilityId, ymd);
        // Untouched for a plan with no daily cap — remainingInDay never stored a budget for it.
        if (key && dayRemaining[key] !== undefined) dayRemaining[key] -= hours;
      }
    };

    return budgets;
  }

  /** Null when this membership covers none of the booking — distinct from a covering zero-hours result. */
  function allocateWithinCandidate(rows, opts, startMs, suites, duration) {
    const facilityTz = opts.facilityTimeZone;
    const plans = getPlans();

    const containingAtStart = findContainingByInstant(rows, startMs);
    if (!containingAtStart) return null;
    const startPeriod = containingAtStart.period;

    const segments = splitSlotBySegments(opts.dateStr, opts.timeStr, duration, facilityTz, rows, startMs);
    if (!segments.length) return null;

    const requestedHours = duration * suites;
    const totalSegmentHours = segments.reduce(function (sum, segment) { return sum + segment.hours; }, 0);
    if (!(totalSegmentHours > 0)) return null;

    // A windowed plan (legacy Unlimited) may not cross local midnight, so only the first day scores.
    const windowedPlan = plans.windowEndMinutesFor(startPeriod) != null;
    const firstYmd = segments[0].ymd;

    const homeFacilityId = startPeriod.facility_id != null ? startPeriod.facility_id : opts.facilityId;
    const budgets = createHourBudgets(homeFacilityId, opts.hoursUsedByPeriod, opts.hoursUsedByDay);

    const byPeriodOrder = [];
    const byPeriodHours = {};
    const byDay = {};
    let fundedHours = 0;

    segments.forEach(function (segment) {
      // Don't borrow startPeriod: a gap or a tail past the last bucket is unpaid.
      const period = segment.period;
      if (!period) return;
      if (windowedPlan && segment.ymd !== firstYmd) return;

      const share = requestedHours * (segment.hours / totalSegmentHours);
      const planGate = planBookableHoursForSegment(period, segment, facilityTz) * suites;
      if (!(planGate > 0)) return;

      const funded = Math.min(share, planGate, budgets.remainingInDay(period, segment.ymd), budgets.remainingInPeriod(period));
      if (!(funded > 0)) return;
      budgets.spend(period, segment.ymd, funded);

      const periodKey = periodBalanceKey(period);
      if (byPeriodHours[periodKey] === undefined) {
        byPeriodHours[periodKey] = 0;
        byPeriodOrder.push({ key: periodKey, period: period });
      }
      byPeriodHours[periodKey] += funded;

      const dayKey = budgets.dayKeyFor(segment.ymd);
      if (dayKey) byDay[dayKey] = (byDay[dayKey] || 0) + funded;
      fundedHours += funded;
    });

    return {
      requestedHours: requestedHours,
      fundedHours: fundedHours,
      byPeriod: byPeriodOrder.map(function (entry) {
        return { period: entry.period, usedHours: byPeriodHours[entry.key] };
      }),
      byDay: byDay,
      segments: segments,
      startPeriod: startPeriod
    };
  }

  /**
   * First candidate that funds anything pays for the whole booking — they never split it.
   * None can fund → first covering candidate at zero hours ("no hours left", not "not a member").
   */
  function allocateMembershipHours(options) {
    const opts = options || {};
    const suites = normalizeSuiteCount(opts.suiteCount);
    const duration = Number(opts.durationHours);
    const empty = { requestedHours: 0, fundedHours: 0, byPeriod: [], byDay: {}, segments: [], startPeriod: null };
    if (!Number.isFinite(duration) || duration <= 0) return empty;
    if (!getPlans()) return empty;

    const startMs = parseSlotUtcMs(opts.dateStr, opts.timeStr, opts.facilityTimeZone);
    if (startMs == null) return empty;
    const endMs = startMs + duration * MS_PER_HOUR;

    const candidates = getMembershipCandidates(opts.entries || [], opts.facilityId, opts.preferredType);
    let firstCovering = null;

    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i];
      if (candidate.minStartMs >= endMs || candidate.maxEndMs <= startMs) continue;

      const allocation = allocateWithinCandidate(candidate.rows, opts, startMs, suites, duration);
      if (!allocation) continue;
      if (allocation.fundedHours > 0) return allocation;
      if (!firstCovering) firstCovering = allocation;
    }

    return firstCovering || empty;
  }

  /** Prorate an allocation down to the backend's ceiling. A missing ceiling means unknown, never zero. */
  function clampAllocationToCeiling(allocation, ceilingHours) {
    if (!allocation) return allocation;
    if (ceilingHours === null || ceilingHours === undefined || ceilingHours === '') return allocation;
    const ceiling = Number(ceilingHours);
    if (!Number.isFinite(ceiling)) return allocation;
    const funded = allocation.fundedHours;
    if (!(funded > 0)) return allocation;

    const target = Math.max(0, ceiling);
    if (funded <= target) return allocation;

    const factor = target / funded;
    const byDay = {};
    Object.keys(allocation.byDay).forEach(function (key) { byDay[key] = allocation.byDay[key] * factor; });

    return Object.assign({}, allocation, {
      fundedHours: target,
      ceilingApplied: true,
      byPeriod: allocation.byPeriod.map(function (entry) {
        return { period: entry.period, usedHours: entry.usedHours * factor };
      }),
      byDay: byDay
    });
  }

  /**
   * Legacy map format: facilityId → remaining hours (old PDP). No periods — one hours pool per facility.
   */
  function calculateSlotDiscountLegacyFacilityMap(membershipData, facilityId, dateStr, timeStr, durationHours, fullPriceCents, hoursUsedInCart, facilityTimeZone, suiteCount, hoursUsedByDay) {
    const suites = normalizeSuiteCount(suiteCount);
    const hoursUsedMap = (typeof hoursUsedInCart === 'object' && hoursUsedInCart !== null) ? hoursUsedInCart : null;
    const result = {
      discountedPriceCents: fullPriceCents,
      membershipHoursUsed: 0,
      paidHours: durationHours * suites,
      canUseMembership: false,
      isUnlimited: false,
      membershipHoursUsedByPeriod: []
    };
    const pool = getLegacyFacilityMapHours(membershipData, facilityId);
    if (pool == null || isNaN(Number(pool))) return result;

    const fidStr = String(Number(facilityId));
    let used = 0;
    if (hoursUsedMap) {
      Object.keys(hoursUsedMap).forEach(function (k) {
        if (k.indexOf(fidStr + '|') === 0) used += Number(hoursUsedMap[k] || 0);
      });
    } else {
      used = Number(hoursUsedInCart || 0);
    }

    const hoursAvail = Math.max(0, Number(pool) - used);
    const duration = Number(durationHours);
    if (isNaN(duration) || duration <= 0 || fullPriceCents <= 0) return result;
    const totalMembershipHoursNeeded = duration * suites;

    // The map carries no type, so the plan is resolved from the pool size alone.
    const poolAsPeriod = { facility_id: Number(facilityId), hours: Number(pool) };
    const unlimited = !planHasHourPool(poolAsPeriod);
    result.isUnlimited = unlimited;

    if (unlimited) {
      const allowance = planFreePaidHours(poolAsPeriod, dateStr, timeStr, durationHours, facilityTimeZone, suites, hoursUsedByDay);
      if (allowance.freeHours <= 0) return result;
      result.canUseMembership = true;
      result.membershipHoursUsed = allowance.freeHours;
      result.paidHours = allowance.paidHours;
      const hourlyRate = fullPriceCents / totalMembershipHoursNeeded;
      result.discountedPriceCents = Math.round(hourlyRate * allowance.paidHours);
      return result;
    }

    const useHours = Math.min(hoursAvail, totalMembershipHoursNeeded);
    if (useHours <= 0) return result;
    result.canUseMembership = true;
    result.membershipHoursUsed = useHours;
    result.paidHours = totalMembershipHoursNeeded - useHours;
    const hourlyRate = fullPriceCents / totalMembershipHoursNeeded;
    result.discountedPriceCents = Math.round(hourlyRate * result.paidHours);
    const syntheticPeriod = { facility_id: Number(facilityId), start: fidStr + '|__legacy_map__', type: 'pro', hours: Number(pool) };
    result.membershipHoursUsedByPeriod.push({ period: syntheticPeriod, usedHours: useHours });
    return result;
  }

  /**
   * The hours metafield is a rolling window that stops at the next renewal, so a live membership
   * routinely has no row for a date the backend still covers. `GET /slots` answers per slot for that
   * exact date, and it is the only source left once the rows say nothing — so here the backend figure
   * grants hours, where everywhere else it only caps them. Callers pass `backendGrant` solely when
   * the grid's `meta.membership` says the membership is active, never from the metafield alone.
   *
   * `hoursUsedByPeriod` / `hoursUsedByDay` are deliberately **not** subtracted here: the backend
   * figure already nets the other cart items out through their live holds, so clamping it again
   * would subtract the same hours twice and under-grant the member. That reasoning depends on the
   * grid having been fetched after the last cart change, which is why `buildBackendGrant` refuses to
   * build a grant at all unless the caller vouches for it (`gridFresh`) — the exception to the
   * "no cart item double-spends a balance" rule is gated, not assumed.
   */
  function applyBackendGrant(result, backendGrant, facilityId, dateStr, facilityTz, totalMembershipHoursNeeded, fullPriceCents) {
    if (!backendGrant) return result;
    const granted = Number(backendGrant.hours);
    if (!Number.isFinite(granted) || granted <= 0 || !(totalMembershipHoursNeeded > 0)) return result;

    const ymd = normalizeInputDateToYmd(dateStr, facilityTz) || toYMD(dateStr);
    if (!ymd) return result;

    // No row means no period object either, so one is fabricated. `start` stays a real UTC instant —
    // the facility-local start of the booking's day — because every other reader date-parses it; the
    // grant is marked by `source`, never by an unparseable sentinel. Anchoring it to the day also
    // makes the period key unique per facility and day, so two cart items on that day are distinct.
    const startZulu = localDayStartZulu(ymd, facilityTz);
    if (!startZulu) return result;

    const period = {
      facility_id: Number(facilityId),
      start: startZulu,
      type: backendGrant.type || 'pro',
      source: 'backend'
    };
    if (backendGrant.dailyLimit != null) period.daily_limit = backendGrant.dailyLimit;
    if (backendGrant.crossFacility === true) period.cross_facility = true;

    const useHours = Math.min(granted, totalMembershipHoursNeeded);
    const dayKey = membershipDayKey(period.facility_id, ymd);

    result.canUseMembership = true;
    result.isUnlimited = isUnlimitedType(period);
    result.backendGranted = true;
    result.period = period;
    result.requestedHours = totalMembershipHoursNeeded;
    result.membershipHoursUsed = useHours;
    result.membershipHoursUsedByPeriod = [{ period: period, usedHours: useHours }];
    result.hoursByDay = dayKey ? { [dayKey]: useHours } : null;
    result.paidHours = Math.max(0, totalMembershipHoursNeeded - useHours);
    result.discountedPriceCents = Math.round((fullPriceCents / totalMembershipHoursNeeded) * result.paidHours);
    return result;
  }

  /**
   * Membership discount for one slot.
   * `hoursUsedByPeriod` / `hoursUsedByDay` are the maps from
   * MembershipSlotPricing.getMembershipHoursUsedInCart, so other cart items can't double-spend a
   * period balance or a daily allowance. `suiteCount` scales the hours (multi-suite).
   * `consumableHoursCeiling` is the backend's ceiling (GET /slots or the hold answer); applying it
   * here keeps the per-period split proportional before anything is rounded.
   */
  function calculateMembershipDiscountForSlot({ membershipData, facilityId, dateStr, timeStr, durationHours, fullPriceCents, hoursUsedByPeriod, hoursUsedByDay, facilityTimeZone, suiteCount, consumableHoursCeiling, preferredType, backendGrant }) {
    const suites = normalizeSuiteCount(suiteCount);
    const hoursUsedMap = (typeof hoursUsedByPeriod === 'object' && hoursUsedByPeriod !== null) ? hoursUsedByPeriod : null;

    const result = {
      discountedPriceCents: fullPriceCents,
      membershipHoursUsed: 0,
      paidHours: durationHours * suites,
      canUseMembership: false,
      isUnlimited: false,
      membershipHoursUsedByPeriod: []
    };
    if (!getPlans()) return result;

    if (getMembershipKind(membershipData) === 'legacy-map') {
      return calculateSlotDiscountLegacyFacilityMap(
        membershipData,
        facilityId,
        dateStr,
        timeStr,
        durationHours,
        fullPriceCents,
        hoursUsedByPeriod,
        facilityTimeZone,
        suites,
        hoursUsedByDay
      );
    }

    const entries = getMembershipRowsForCalc(membershipData);
    const duration = Number(durationHours);
    const totalMembershipHoursNeeded = duration * suites;
    const grant = () => applyBackendGrant(result, backendGrant, facilityId, dateStr, facilityTimeZone, totalMembershipHoursNeeded, fullPriceCents);

    // Every "no local coverage" exit goes through the grant: an empty metafield is exactly the case
    // where the backend is the only source, so it must not fall out before the fallback.
    if (!entries.length) return grant();

    const slotMs = facilityTimeZone && hasDayjsTz()
      ? parseSlotUtcMs(dateStr, timeStr, facilityTimeZone)
      : null;

    if (slotMs == null || !(duration > 0) || !hasDayjsTz()) {
      const legacy = getPeriodForFacilityAndDateTimeLegacy(entries, facilityId, dateStr, timeStr);
      if (!legacy) return grant();
      return calculateMembershipDiscountLegacySlot(legacy, dateStr, timeStr, durationHours, fullPriceCents, hoursUsedMap, hoursUsedByPeriod, result, facilityTimeZone, suites, hoursUsedByDay);
    }

    const allocation = allocateMembershipHours({
      entries: entries,
      facilityId: facilityId,
      dateStr: dateStr,
      timeStr: timeStr,
      durationHours: duration,
      suiteCount: suites,
      facilityTimeZone: facilityTimeZone,
      hoursUsedByPeriod: hoursUsedByPeriod,
      hoursUsedByDay: hoursUsedByDay,
      preferredType: preferredType
    });
    if (!allocation.startPeriod) return grant();

    const applied = clampAllocationToCeiling(allocation, consumableHoursCeiling);
    result.isUnlimited = isUnlimitedType(allocation.startPeriod);
    result.period = allocation.startPeriod;
    result.requestedHours = applied.requestedHours;
    result.hoursByDay = applied.byDay;
    result.ceilingApplied = applied.ceilingApplied === true;

    if (applied.fundedHours > 0) {
      result.canUseMembership = true;
      result.membershipHoursUsed = applied.fundedHours;
      result.membershipHoursUsedByPeriod = applied.byPeriod.slice();
    }

    result.paidHours = Math.max(0, totalMembershipHoursNeeded - result.membershipHoursUsed);
    const hourlyRate = fullPriceCents / totalMembershipHoursNeeded;
    result.discountedPriceCents = Math.round(hourlyRate * result.paidHours);
    return result;
  }

  function getHoursInCurrentPeriodLegacy(period, nextEntry, slotDateStr, slotTimeStr, durationHours) {
    const slotDateOnly = toPeriodDateStr(slotDateStr);
    const periodEndDateStr = getPeriodEndDateStr(period, nextEntry, null);
    const slotStartMinutes = timeToMinutes(slotTimeStr);
    const slotEndMinutes = slotStartMinutes + Math.round(durationHours * 60);
    if (periodEndDateStr === '' || slotDateOnly < periodEndDateStr) {
      return durationHours;
    }
    if (slotDateOnly > periodEndDateStr) return 0;
    const periodEndMinutes = getMinutesFromStartIso(period.start);
    if (slotStartMinutes >= periodEndMinutes) return 0;
    const minutesInPeriod = Math.min(slotEndMinutes - slotStartMinutes, periodEndMinutes - slotStartMinutes);
    return Math.max(0, minutesInPeriod / 60);
  }

  function calculateMembershipDiscountLegacySlot(legacy, dateStr, timeStr, durationHours, fullPriceCents, hoursUsedMap, hoursUsedInCart, result, facilityTimeZone, suiteCount, hoursUsedByDay) {
    const suites = normalizeSuiteCount(suiteCount);
    const totalMembershipHoursNeeded = Number(durationHours) * suites;
    const period = legacy.period;
    const nextEntry = legacy.nextEntry;
    const unlimited = isUnlimitedType(period);
    result.isUnlimited = unlimited;

    if (unlimited) {
      const allowance = planFreePaidHours(period, dateStr, timeStr, durationHours, facilityTimeZone, suites, hoursUsedByDay);
      if (allowance.freeHours <= 0) return result;
      result.canUseMembership = true;
      result.membershipHoursUsed = allowance.freeHours;
      result.paidHours = allowance.paidHours;
      const hourlyRate = fullPriceCents / totalMembershipHoursNeeded;
      result.discountedPriceCents = Math.round(hourlyRate * allowance.paidHours);
      return result;
    }

    // Only the period covering the slot start pays; time past its end is charged.
    const hoursInCurrentPeriod = getHoursInCurrentPeriodLegacy(period, nextEntry, dateStr, timeStr, durationHours);
    const getUsedHoursForPeriod = function (p) {
      if (!p) return 0;
      if (!hoursUsedMap) return Number(hoursUsedInCart || 0);
      return Number(hoursUsedMap[periodBalanceKey(p)] || 0);
    };

    const allowance = planFreePaidHours(period, dateStr, timeStr, hoursInCurrentPeriod, facilityTimeZone, suites, hoursUsedByDay);
    const availableInPeriod = getAvailableHoursForPeriod(period, getUsedHoursForPeriod(period));
    const usedHours = (availableInPeriod != null && availableInPeriod > 0)
      ? Math.min(availableInPeriod, allowance.freeHours)
      : 0;

    if (usedHours > 0) {
      result.canUseMembership = true;
      result.membershipHoursUsed = usedHours;
      result.membershipHoursUsedByPeriod.push({ period: period, usedHours: usedHours });
    }

    result.paidHours = totalMembershipHoursNeeded - result.membershipHoursUsed;
    const hourlyRate = fullPriceCents / totalMembershipHoursNeeded;
    result.discountedPriceCents = Math.round(hourlyRate * result.paidHours);
    return result;
  }

  /** Empty answer shared by the hours lookups, so callers always get the same shape. */
  function emptyMembershipHoursInfo() {
    return { hours: null, periodStart: null, type: 'pro', planKey: null, hasHourPool: true, dailyLimit: null, period: null };
  }

  /**
   * Hours available on a date. `hours === null` means the plan has no pool — check `hasHourPool`
   * rather than the type string, which now carries the real plan key (starter/pro/elite/...).
   */
  function getMembershipHoursForDate(membershipData, facilityId, dateStr, hoursUsedInCart, facilityTz, preferredType) {
    const out = emptyMembershipHoursInfo();
    const plans = getPlans();
    if (!plans) return out;

    if (getMembershipKind(membershipData) === 'legacy-map') {
      const pool = getLegacyFacilityMapHours(membershipData, facilityId);
      if (pool == null || isNaN(Number(pool))) return out;
      const poolAsPeriod = { facility_id: Number(facilityId), hours: Number(pool) };
      out.period = poolAsPeriod;
      out.planKey = plans ? plans.resolve(poolAsPeriod).key : 'pro';
      out.type = out.planKey;
      out.hasHourPool = planHasHourPool(poolAsPeriod);
      out.dailyLimit = plans ? plans.dailyLimitFor(poolAsPeriod) : null;
      if (!out.hasHourPool) return out;
      out.hours = Math.max(0, Number(pool) - Number(hoursUsedInCart || 0));
      return out;
    }

    const entries = getMembershipRowsForCalc(membershipData);
    if (!entries.length) return out;

    const period = getPeriodForFacilityAndDate(entries, facilityId, dateStr, facilityTz, preferredType);
    if (!period) return out;

    out.period = period;
    out.planKey = plans ? plans.resolve(period).key : 'pro';
    out.type = out.planKey;
    out.hasHourPool = planHasHourPool(period);
    out.dailyLimit = plans ? plans.dailyLimitFor(period) : null;
    out.periodStart = period.start || null;
    if (!out.hasHourPool) return out;
    out.hours = getAvailableHoursForPeriod(period, hoursUsedInCart);
    return out;
  }

  function getMembershipHoursForToday(membershipData, facilityId, hoursUsedInCart, facilityTz, preferredType) {
    const ymd = currentYmdViaDayjs(facilityTz);
    if (!ymd) return emptyMembershipHoursInfo();
    return getMembershipHoursForDate(membershipData, facilityId, ymd, hoursUsedInCart, facilityTz, preferredType);
  }

  /**
   * Period covering a date, restricted to one plan family: `'unlimited'` selects plans without an
   * hour pool, any other value selects the pooled plans (starter/pro/elite/executive).
   */
  function getPeriodForFacilityAndDateByType(entries, facilityId, date, type, facilityTz) {
    const wantUnlimited = type === 'unlimited';
    const ymd = normalizeInputDateToYmd(date, facilityTz);
    if (!ymd) return null;

    const dayRange = facilityTz && hasDayjsTz() ? localDayBoundsUtcMs(ymd, facilityTz) : null;

    return firstCandidateHit(entries, facilityId, function (rows) {
      const ofFamily = rows.filter(function (e) { return isUnlimitedType(e) === wantUnlimited; });
      if (!ofFamily.length) return null;
      if (dayRange) {
        const hit = findContainingLocalDay(ofFamily, ymd, facilityTz, dayRange);
        if (hit) return hit;
      }
      return findPeriodByDateStr(ofFamily, ymd);
    }, { ymd: ymd, facilityTz: facilityTz });
  }

  const api = {
    roundHours: roundHours,
    isNewMembershipFormat: isNewMembershipFormat,
    getMembershipKind: getMembershipKind,
    hasStructuredMembershipRows: hasStructuredMembershipRows,
    hasMembershipSlotData: hasMembershipSlotData,
    getLegacyFacilityMapHours: getLegacyFacilityMapHours,
    unwrapMembershipValue: unwrapMembershipValue,
    getMembershipEntries: getMembershipEntries,
    getFacilityIdFromProduct: getFacilityIdFromProduct,
    getPeriodForFacilityAndDate: getPeriodForFacilityAndDate,
    getPeriodEndYmd: getPeriodEndYmd,
    getPeriodForFacilityAndDateByType: getPeriodForFacilityAndDateByType,
    isUnlimitedType: isUnlimitedType,
    getAvailableHoursForPeriod: getAvailableHoursForPeriod,
    isWeekday: isWeekday,
    planFreePaidHours: planFreePaidHours,
    splitSlotBySegments: splitSlotBySegments,
    allocateMembershipHours: allocateMembershipHours,
    clampAllocationToCeiling: clampAllocationToCeiling,
    membershipDayKey: membershipDayKey,
    ymdForInstantInZone: ymdForInstantInZone,
    calculateMembershipDiscountForSlot: calculateMembershipDiscountForSlot,
    getMembershipHoursForDate: getMembershipHoursForDate,
    getMembershipHoursForToday: getMembershipHoursForToday,
    getMembershipCandidates: getMembershipCandidates,
    isCandidateRankingCertain: isCandidateRankingCertain
  };

  if (typeof window !== 'undefined') window.MembershipHoursUtils = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
